import {
  AlertTriangle,
  BarChart3,
  Ban,
  CheckCircle2,
  CheckSquare,
  Clock3,
  LineChart,
  RefreshCw,
  Search,
  ShieldCheck,
  Store,
  UserCog,
  Users,
  X,
  XCircle
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type {
  PortalAppRoleAssignment,
  PortalAccessAdminSnapshot,
  PortalRoleAdminSnapshot,
  PortalRolePrincipal,
  PortalAccessAdminUser,
  PortalAccessStatus,
  PortalStationAccessRole,
  PortalUsageRange,
  PortalUsageSnapshot
} from "../../shared/types";
import { useAuthUser } from "../auth/AuthProvider";
import { StateBlock } from "../components/common/StateBlock";
import {
  getPortalAccessAdminSnapshot,
  getPortalRoleAdminSnapshot,
  grantPortalAppRole,
  grantPortalStationAccess,
  getPortalUsageSnapshot,
  revokePortalAppRoleAssignment,
  revokePortalStationAccess,
  searchPortalRolePrincipals,
  updatePortalUserStatus
} from "../services/portalService";

type EditableAccessStatus = Exclude<PortalAccessStatus, "SETUP_REQUIRED">;
type AdminTab = "users" | "roles" | "usage";
type UserFilter = "ALL" | EditableAccessStatus | "NO_STATION";

const accessStatuses: EditableAccessStatus[] = ["PENDING", "ACTIVE", "SUSPENDED", "DENIED"];
const accessRoles: PortalStationAccessRole[] = ["VIEWER", "MANAGER", "OWNER"];
const filterLabels: Record<UserFilter, string> = {
  ALL: "All",
  PENDING: "Pending",
  ACTIVE: "Active",
  SUSPENDED: "Suspended",
  DENIED: "Denied",
  NO_STATION: "No station"
};

function statusClass(status: EditableAccessStatus): string {
  return `access-status-${status.toLowerCase()}`;
}

function formatDate(value?: string): string {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleString();
}

function formatShortDate(value?: string): string {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleDateString(undefined, { month: "short", day: "2-digit" });
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}

function formatDuration(value?: number): string {
  if (!value || !Number.isFinite(value)) {
    return "-";
  }

  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}s`;
  }

  return `${Math.round(value)}ms`;
}

function userIdentity(user: PortalAccessAdminUser): string {
  return user.email || user.userPrincipalName || user.entraObjectId;
}

function activeStationCount(user: PortalAccessAdminUser): number {
  return user.stations.filter((station) => station.isActive).length;
}

function principalIdentity(principal: PortalRolePrincipal): string {
  return principal.mail || principal.userPrincipalName || principal.id;
}

function assignmentIdentity(assignment: PortalAppRoleAssignment): string {
  return `${assignment.principalDisplayName}-${assignment.appRoleId}`;
}

function firstUserId(users: PortalAccessAdminUser[]): string | undefined {
  return users.find((user) => user.accessStatus === "PENDING")?.entraObjectId || users[0]?.entraObjectId;
}

function stationName(station: { stationSk: number; stationName?: string; businessUnitName?: string }): string {
  return station.stationName || station.businessUnitName || `Station ${station.stationSk}`;
}

function stationMeta(station: { businessUnitName?: string; province?: string; address?: string }): string {
  return [station.businessUnitName, station.province, station.address].filter(Boolean).join(" - ") || "Station";
}

function AccessStatusChip({ status }: { status: EditableAccessStatus }) {
  return <span className={`status-chip ${statusClass(status)}`}>{status}</span>;
}

export function AccessAdminPage() {
  const { portalContext, userLabel } = useAuthUser();
  const [snapshot, setSnapshot] = useState<PortalAccessAdminSnapshot>();
  const [roleSnapshot, setRoleSnapshot] = useState<PortalRoleAdminSnapshot>();
  const [activeTab, setActiveTab] = useState<AdminTab>("users");
  const [userFilter, setUserFilter] = useState<UserFilter>("PENDING");
  const [selectedUserId, setSelectedUserId] = useState<string>();
  const [searchTerm, setSearchTerm] = useState("");
  const [stationSearchTerm, setStationSearchTerm] = useState("");
  const [hideAssignedStations, setHideAssignedStations] = useState(true);
  const [principalSearchTerm, setPrincipalSearchTerm] = useState("");
  const [principalResults, setPrincipalResults] = useState<PortalRolePrincipal[]>([]);
  const [selectedPrincipalId, setSelectedPrincipalId] = useState<string>();
  const [selectedRoleId, setSelectedRoleId] = useState<string>();
  const [selectedStationSks, setSelectedStationSks] = useState<string[]>([]);
  const [accessRole, setAccessRole] = useState<PortalStationAccessRole>("VIEWER");
  const [usageRange, setUsageRange] = useState<PortalUsageRange>("30d");
  const [usageSnapshot, setUsageSnapshot] = useState<PortalUsageSnapshot>();
  const [isUsageLoading, setIsUsageLoading] = useState(false);
  const usageRequestId = useRef(0);
  const [isLoading, setIsLoading] = useState(true);
  const [busyAction, setBusyAction] = useState<string>();
  const [roleBusyAction, setRoleBusyAction] = useState<string>();
  const [error, setError] = useState<string>();
  const [message, setMessage] = useState<string>();
  const [roleError, setRoleError] = useState<string>();
  const [usageError, setUsageError] = useState<string>();

  const users = snapshot?.users || [];
  const stations = snapshot?.stations || [];
  const roles = roleSnapshot?.roles || [];
  const assignments = roleSnapshot?.assignments || [];
  const maxDailyActivity = Math.max(
    1,
    ...(usageSnapshot?.daily || []).map((day) => Math.max(day.successfulSignIns, day.apiRequests, day.pageViews))
  );

  const counts = useMemo(() => ({
    total: users.length,
    pending: users.filter((user) => user.accessStatus === "PENDING").length,
    active: users.filter((user) => user.accessStatus === "ACTIVE").length,
    suspended: users.filter((user) => user.accessStatus === "SUSPENDED").length,
    denied: users.filter((user) => user.accessStatus === "DENIED").length,
    noStation: users.filter((user) => activeStationCount(user) === 0).length,
    assigned: users.reduce((sum, user) => sum + activeStationCount(user), 0)
  }), [users]);

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return users.filter((user) => {
      const matchesFilter = userFilter === "ALL"
        || user.accessStatus === userFilter
        || (userFilter === "NO_STATION" && activeStationCount(user) === 0);

      if (!matchesFilter) {
        return false;
      }

      if (!term) {
        return true;
      }

      return [
        user.displayName,
        user.email,
        user.userPrincipalName,
        user.accessStatus,
        user.entraObjectId,
        ...user.stations.flatMap((station) => [
          String(station.stationSk),
          station.stationName,
          station.businessUnitName,
          station.province
        ])
      ].some((value) => value?.toLowerCase().includes(term));
    });
  }, [searchTerm, userFilter, users]);

  const selectedUser = filteredUsers.find((user) => user.entraObjectId === selectedUserId)
    || filteredUsers[0]
    || users.find((user) => user.entraObjectId === selectedUserId)
    || users[0];
  const selectedPrincipal = principalResults.find((principal) => principal.id === selectedPrincipalId);

  const activeStationSks = useMemo(() => new Set(
    selectedUser?.stations
      .filter((station) => station.isActive)
      .map((station) => String(station.stationSk)) || []
  ), [selectedUser]);

  const filteredStations = useMemo(() => {
    const term = stationSearchTerm.trim().toLowerCase();

    return stations.filter((station) => {
      const stationSkValue = String(station.stationSk);
      if (hideAssignedStations && activeStationSks.has(stationSkValue)) {
        return false;
      }

      if (!term) {
        return true;
      }

      return [
        stationSkValue,
        station.stationName,
        station.businessUnitName,
        station.province,
        station.address
      ].some((value) => value?.toLowerCase().includes(term));
    });
  }, [activeStationSks, hideAssignedStations, stationSearchTerm, stations]);

  const selectableFilteredStationSks = useMemo(() => filteredStations
    .filter((station) => !activeStationSks.has(String(station.stationSk)))
    .map((station) => String(station.stationSk)), [activeStationSks, filteredStations]);

  const roleCounts = useMemo(() => roles.map((role) => ({
    role,
    count: assignments.filter((assignment) => assignment.appRoleId === role.id).length
  })), [assignments, roles]);

  const filterOptions = useMemo<Array<{ id: UserFilter; count: number }>>(() => [
    { id: "PENDING", count: counts.pending },
    { id: "NO_STATION", count: counts.noStation },
    { id: "ACTIVE", count: counts.active },
    { id: "SUSPENDED", count: counts.suspended },
    { id: "DENIED", count: counts.denied },
    { id: "ALL", count: counts.total }
  ], [counts]);

  async function loadAccess() {
    setIsLoading(true);
    setError(undefined);
    setRoleError(undefined);
    const [accessResult, roleResult] = await Promise.allSettled([
      getPortalAccessAdminSnapshot(),
      getPortalRoleAdminSnapshot()
    ]);

    if (accessResult.status === "fulfilled") {
      setSnapshot(accessResult.value);
      setSelectedUserId((current) => current && accessResult.value.users.some((user) => user.entraObjectId === current)
        ? current
        : firstUserId(accessResult.value.users));
    } else {
      setError(accessResult.reason instanceof Error ? accessResult.reason.message : "Unable to load portal access users.");
    }

    if (roleResult.status === "fulfilled") {
      setRoleSnapshot(roleResult.value);
      setSelectedRoleId((current) => current && roleResult.value.roles.some((role) => role.id === current)
        ? current
        : roleResult.value.roles.find((role) => role.value === "Marketing.Analytics")?.id || roleResult.value.roles[0]?.id);
    } else {
      setRoleError(roleResult.reason instanceof Error ? roleResult.reason.message : "Unable to load App Role assignments.");
    }

    setIsLoading(false);
  }

  async function refreshRoles() {
    setRoleError(undefined);
    try {
      const nextRoleSnapshot = await getPortalRoleAdminSnapshot();
      setRoleSnapshot(nextRoleSnapshot);
      setSelectedRoleId((current) => current && nextRoleSnapshot.roles.some((role) => role.id === current)
        ? current
        : nextRoleSnapshot.roles[0]?.id);
    } catch (loadError) {
      setRoleError(loadError instanceof Error ? loadError.message : "Unable to load App Role assignments.");
    }
  }

  async function loadUsage(range = usageRange, refresh = false) {
    const requestId = ++usageRequestId.current;
    setIsUsageLoading(true);
    setUsageError(undefined);
    try {
      const snapshot = await getPortalUsageSnapshot(range, refresh);
      if (requestId === usageRequestId.current) setUsageSnapshot(snapshot);
    } catch (loadError) {
      if (requestId === usageRequestId.current) setUsageError(loadError instanceof Error ? loadError.message : "Unable to load usage analytics.");
    } finally {
      if (requestId === usageRequestId.current) setIsUsageLoading(false);
    }
  }

  async function runMutation(action: string, mutation: () => Promise<PortalAccessAdminSnapshot>) {
    setBusyAction(action);
    setError(undefined);
    setMessage(undefined);
    try {
      const nextSnapshot = await mutation();
      setSnapshot(nextSnapshot);
      setSelectedUserId((current) => current && nextSnapshot.users.some((user) => user.entraObjectId === current)
        ? current
        : firstUserId(nextSnapshot.users));
      setMessage("Access control updated.");
    } catch (mutationError) {
      setError(mutationError instanceof Error ? mutationError.message : "Access control update failed.");
    } finally {
      setBusyAction(undefined);
    }
  }

  async function runRoleMutation(action: string, mutation: () => Promise<PortalRoleAdminSnapshot>) {
    setRoleBusyAction(action);
    setRoleError(undefined);
    setMessage(undefined);
    try {
      const nextRoleSnapshot = await mutation();
      setRoleSnapshot(nextRoleSnapshot);
      setMessage("Directory role assignment updated.");
    } catch (mutationError) {
      setRoleError(mutationError instanceof Error ? mutationError.message : "Directory role assignment update failed.");
    } finally {
      setRoleBusyAction(undefined);
    }
  }

  function setStatus(accessStatus: EditableAccessStatus) {
    if (!selectedUser) {
      return;
    }

    if (accessStatus === "ACTIVE" && activeStationCount(selectedUser) === 0) {
      const shouldContinue = window.confirm("This user has no active Station_SK assignments. Activate anyway?");
      if (!shouldContinue) {
        return;
      }
    }

    if ((accessStatus === "SUSPENDED" || accessStatus === "DENIED") && !window.confirm(`Set ${selectedUser.displayName} to ${accessStatus}?`)) {
      return;
    }

    void runMutation(`status-${accessStatus}`, () => updatePortalUserStatus(selectedUser.entraObjectId, accessStatus));
  }

  function grantStation() {
    if (!selectedUser || selectedStationSks.length === 0) {
      setError("Select at least one Station_SK before granting access.");
      return;
    }

    void runMutation("grant-stations", async () => {
      const nextSnapshot = await grantPortalStationAccess(selectedUser.entraObjectId, selectedStationSks.map(Number), accessRole);
      setSelectedStationSks([]);
      return nextSnapshot;
    });
  }

  function toggleStationSk(stationSk: number) {
    const nextValue = String(stationSk);
    setSelectedStationSks((current) => current.includes(nextValue)
      ? current.filter((value) => value !== nextValue)
      : [...current, nextValue]);
  }

  function selectFilteredStations() {
    setSelectedStationSks((current) => [...new Set([...current, ...selectableFilteredStationSks])]);
  }

  function revokeStation(accessId: string) {
    if (!selectedUser || !window.confirm("Revoke this station assignment?")) {
      return;
    }

    void runMutation(`revoke-${accessId}`, () => revokePortalStationAccess(selectedUser.entraObjectId, accessId));
  }

  function grantRole() {
    if (!selectedPrincipalId || !selectedRoleId) {
      setRoleError("Select a user or group and an App Role before granting access.");
      return;
    }

    void runRoleMutation("grant-role", () => grantPortalAppRole(selectedPrincipalId, selectedRoleId));
  }

  function revokeRole(assignmentId: string) {
    if (!window.confirm("Revoke this App Role assignment?")) {
      return;
    }

    void runRoleMutation(`revoke-role-${assignmentId}`, () => revokePortalAppRoleAssignment(assignmentId));
  }

  useEffect(() => {
    void loadAccess();
  }, []);

  useEffect(() => {
    if (activeTab === "usage") {
      void loadUsage(usageRange);
    }
  }, [activeTab, usageRange]);

  useEffect(() => {
    const term = principalSearchTerm.trim();
    setSelectedPrincipalId(undefined);
    if (term.length < 2) {
      setPrincipalResults([]);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      searchPortalRolePrincipals(term)
        .then((result) => {
          setPrincipalResults(result.principals);
          setRoleError(undefined);
        })
        .catch((searchError) => {
          setPrincipalResults([]);
          setRoleError(searchError instanceof Error ? searchError.message : "Directory principal search failed.");
        });
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [principalSearchTerm]);

  useEffect(() => {
    setSelectedStationSks([]);
    setStationSearchTerm("");
  }, [selectedUser?.entraObjectId]);

  if (!portalContext?.features.stationAccessAdmin) {
    return <StateBlock type="error" title="Access admin unavailable" message="Your account is not assigned to portal access administration." />;
  }

  if (isLoading) {
    return <StateBlock type="loading" title="Loading access control" message="Preparing portal user access records." />;
  }

  if (!snapshot) {
    return <StateBlock type="error" title="Access control unavailable" message={error || "Portal access records could not be loaded."} />;
  }

  return (
    <section className="page-stack">
      <div className="page-heading">
        <div>
          <p>Access administration</p>
          <h2>User Access Control</h2>
        </div>
        <div className="page-heading-actions">
          <span className="success-pill">
            <ShieldCheck size={15} aria-hidden="true" />
            {userLabel}
          </span>
          <button className="ghost-button" disabled={Boolean(busyAction)} onClick={loadAccess} type="button">
            <RefreshCw size={16} aria-hidden="true" />
            Refresh
          </button>
        </div>
      </div>

      <div className="access-summary-grid">
        <button
          className={userFilter === "PENDING" && activeTab === "users" ? "metric-card access-summary-card active" : "metric-card access-summary-card"}
          onClick={() => {
            setActiveTab("users");
            setUserFilter("PENDING");
          }}
          type="button"
        >
          <span className="metric-card__topline">Pending</span>
          <strong>{counts.pending}</strong>
          <p>waiting review</p>
        </button>
        <button
          className={userFilter === "NO_STATION" && activeTab === "users" ? "metric-card access-summary-card active" : "metric-card access-summary-card"}
          onClick={() => {
            setActiveTab("users");
            setUserFilter("NO_STATION");
          }}
          type="button"
        >
          <span className="metric-card__topline">No station</span>
          <strong>{counts.noStation}</strong>
          <p>need assignments</p>
        </button>
        <button
          className={userFilter === "ACTIVE" && activeTab === "users" ? "metric-card access-summary-card active" : "metric-card access-summary-card"}
          onClick={() => {
            setActiveTab("users");
            setUserFilter("ACTIVE");
          }}
          type="button"
        >
          <span className="metric-card__topline">Active</span>
          <strong>{counts.active}</strong>
          <p>{counts.assigned} station grants</p>
        </button>
        <button
          className={activeTab === "roles" ? "metric-card access-summary-card active" : "metric-card access-summary-card"}
          onClick={() => setActiveTab("roles")}
          type="button"
        >
          <span className="metric-card__topline">Directory roles</span>
          <strong>{assignments.length}</strong>
          <p>managed app roles</p>
        </button>
        <button
          className={activeTab === "usage" ? "metric-card access-summary-card active" : "metric-card access-summary-card"}
          onClick={() => setActiveTab("usage")}
          type="button"
        >
          <span className="metric-card__topline">Usage analytics</span>
          <strong>{usageSnapshot?.summary.signInStatus === "unavailable" ? "N/A" : usageSnapshot ? usageSnapshot.summary.distinctSignInUsers : "-"}</strong>
          <p>{usageSnapshot?.summary.signInStatus === "unavailable" ? "sign-in data unavailable"
            : usageSnapshot ? `${formatNumber(usageSnapshot.summary.successfulSignIns)} sign-ins${usageSnapshot.summary.signInStatus === "partial" ? " (partial)" : ""}` : "sign-ins and API use"}</p>
        </button>
      </div>

      {(error || message) && (
        <div className={error ? "api-banner access-admin-feedback" : "access-admin-feedback success-feedback"} role="status">
          <span>{error || message}</span>
        </div>
      )}

      <div className="access-mode-tabs" role="tablist" aria-label="Access administration views">
        <button
          aria-selected={activeTab === "users"}
          className={activeTab === "users" ? "access-mode-tab active" : "access-mode-tab"}
          onClick={() => setActiveTab("users")}
          role="tab"
          type="button"
        >
          <Users size={16} aria-hidden="true" />
          User access
        </button>
        <button
          aria-selected={activeTab === "roles"}
          className={activeTab === "roles" ? "access-mode-tab active" : "access-mode-tab"}
          onClick={() => setActiveTab("roles")}
          role="tab"
          type="button"
        >
          <ShieldCheck size={16} aria-hidden="true" />
          Directory roles
        </button>
        <button
          aria-selected={activeTab === "usage"}
          className={activeTab === "usage" ? "access-mode-tab active" : "access-mode-tab"}
          onClick={() => setActiveTab("usage")}
          role="tab"
          type="button"
        >
          <BarChart3 size={16} aria-hidden="true" />
          Usage analytics
        </button>
      </div>

      {activeTab === "usage" ? (
        <section className="panel usage-admin-panel">
          <div className="panel-heading usage-admin-heading">
            <div>
              <p>User activity</p>
              <h3>Access Usage Analytics</h3>
            </div>
            <div className="usage-admin-actions">
              <div className="gantt-group-control" aria-label="Usage range">
                {(["7d", "30d", "90d"] as PortalUsageRange[]).map((range) => (
                  <button
                    className={usageRange === range ? "active" : ""}
                    key={range}
                    onClick={() => setUsageRange(range)}
                    type="button"
                  >
                    {range.toUpperCase()}
                  </button>
                ))}
              </div>
              <button className="ghost-button" disabled={isUsageLoading} onClick={() => loadUsage(usageRange, true)} type="button">
                <RefreshCw size={16} aria-hidden="true" />
                Refresh usage
              </button>
            </div>
          </div>

          {usageError && (
            <div className="api-banner access-admin-feedback" role="status">
              <span>{usageError}</span>
            </div>
          )}

          {isUsageLoading && !usageSnapshot ? (
            <StateBlock type="loading" title="Loading usage analytics" message="Reading Entra sign-ins and Application Insights telemetry." />
          ) : usageSnapshot ? (
            <>
              <div className="usage-kpi-grid">
                <div className="usage-kpi-card">
                  <span>Successful sign-ins</span>
                  <strong>{usageSnapshot.summary.signInStatus === "unavailable" ? "N/A" : formatNumber(usageSnapshot.summary.successfulSignIns)}</strong>
                  <small>{usageSnapshot.summary.signInStatus === "unavailable" ? "Sign-in data unavailable"
                    : `${formatNumber(usageSnapshot.summary.distinctSignInUsers)} users${usageSnapshot.summary.signInStatus === "partial" ? " (partial)" : ""}`}</small>
                </div>
                <div className="usage-kpi-card">
                  <span>API requests</span>
                  <strong>{formatNumber(usageSnapshot.summary.apiRequests)}</strong>
                  <small>{formatNumber(usageSnapshot.summary.failedApiRequests)} failed</small>
                </div>
                <div className="usage-kpi-card">
                  <span>Page views</span>
                  <strong>{formatNumber(usageSnapshot.summary.pageViews)}</strong>
                  <small>{formatNumber(usageSnapshot.summary.distinctTelemetryUsers)} telemetry users</small>
                </div>
                <div className="usage-kpi-card">
                  <span>Range</span>
                  <strong>{usageSnapshot.summary.range.toUpperCase()}</strong>
                  <small>{formatShortDate(usageSnapshot.summary.rangeStartUtc)} - {formatShortDate(usageSnapshot.summary.rangeEndUtc)}</small>
                  <small>{usageSnapshot.summary.cached ? "Cached" : "Live"} {usageSnapshot.summary.refreshedAtUtc ? `- ${formatDate(usageSnapshot.summary.refreshedAtUtc)}` : ""}</small>
                </div>
              </div>

              {usageSnapshot.warnings.length > 0 && (
                <div className="access-warning usage-warning" role="status">
                  <AlertTriangle size={16} aria-hidden="true" />
                  <span>
                    {usageSnapshot.warnings[0]}
                    {usageSnapshot.warnings.length > 1 ? ` (${usageSnapshot.warnings.length} warnings)` : ""}
                  </span>
                </div>
              )}

              <div className="usage-dashboard-grid">
                <section className="usage-card">
                  <div className="station-picker-heading">
                    <span>Daily activity</span>
                    <strong>{usageSnapshot.daily.length} days</strong>
                  </div>
                  <div className="usage-daily-chart">
                    {usageSnapshot.daily.map((day) => (
                      <div className="usage-daily-row" key={day.date}>
                        <span>{formatShortDate(`${day.date}T00:00:00Z`)}</span>
                        <div className="usage-bar-track" title={`${day.signInsAvailable === false ? "Sign-ins unavailable" : `${day.successfulSignIns} sign-ins`}, ${day.apiRequests} API requests`}>
                          <i className="usage-bar usage-bar--api" style={{ width: `${Math.max(2, (day.apiRequests / maxDailyActivity) * 100)}%` }} />
                          <i className="usage-bar usage-bar--signin" style={{ width: `${Math.max(day.successfulSignIns ? 2 : 0, (day.successfulSignIns / maxDailyActivity) * 100)}%` }} />
                          <i className="usage-bar usage-bar--page" style={{ width: `${Math.max(day.pageViews ? 2 : 0, (day.pageViews / maxDailyActivity) * 100)}%` }} />
                        </div>
                        <strong>{day.signInsAvailable === false ? "N/A" : formatNumber(day.successfulSignIns)}</strong>
                      </div>
                    ))}
                  </div>
                  <div className="usage-legend">
                    <span><i className="usage-dot usage-dot--signin" />Sign-ins</span>
                    <span><i className="usage-dot usage-dot--api" />API</span>
                    <span><i className="usage-dot usage-dot--page" />Pages</span>
                  </div>
                </section>

                <section className="usage-card">
                  <div className="station-picker-heading">
                    <span>User sign-ins</span>
                    <strong>{usageSnapshot.summary.signInStatus === "unavailable" ? "N/A"
                      : `${usageSnapshot.users.length} users${usageSnapshot.summary.signInStatus === "partial" ? " (partial)" : ""}`}</strong>
                  </div>
                  <div className="usage-user-list">
                    {usageSnapshot.users.length === 0 ? (
                      <div className="empty-inline">{usageSnapshot.summary.signInStatus === "unavailable"
                        ? "Entra sign-in data is temporarily unavailable."
                        : usageSnapshot.summary.signInStatus === "partial" ? "No sign-ins in the available data. Some dates could not be loaded."
                          : "No Entra sign-ins found for this range."}</div>
                    ) : usageSnapshot.users.map((user) => (
                      <div className="usage-user-row" key={user.userPrincipalName}>
                        <span>
                          <strong>{user.displayName}</strong>
                          <small>{user.userPrincipalName}</small>
                        </span>
                        <em>{formatNumber(user.signIns)}</em>
                        <small>{formatShortDate(user.firstSignInUtc)} - {formatShortDate(user.lastSignInUtc)}</small>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="usage-card">
                  <div className="station-picker-heading">
                    <span>Top API activity</span>
                    <strong>{usageSnapshot.endpoints.length} endpoints</strong>
                  </div>
                  <div className="usage-table-scroll">
                    <table className="data-table compact usage-table">
                      <thead>
                        <tr>
                          <th>Endpoint</th>
                          <th>Requests</th>
                          <th>Failed</th>
                          <th>Avg</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usageSnapshot.endpoints.map((endpoint) => (
                          <tr key={`${endpoint.name}-${endpoint.url || "none"}`}>
                            <td>
                              <strong>{endpoint.name}</strong>
                              <small>{endpoint.url || "-"}</small>
                            </td>
                            <td>{formatNumber(endpoint.requests)}</td>
                            <td>{formatNumber(endpoint.failed)}</td>
                            <td>{formatDuration(endpoint.avgDurationMs)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="usage-card">
                  <div className="station-picker-heading">
                    <span>Page engagement</span>
                    <strong>{usageSnapshot.pages.length} pages</strong>
                  </div>
                  {usageSnapshot.pages.length === 0 ? (
                    <div className="empty-inline usage-empty">
                      <LineChart size={18} aria-hidden="true" />
                      Page-level telemetry has not been collected yet.
                    </div>
                  ) : (
                    <div className="usage-table-scroll">
                      <table className="data-table compact usage-table">
                        <thead>
                          <tr>
                            <th>Page</th>
                            <th>Views</th>
                            <th>Users</th>
                            <th>Sessions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usageSnapshot.pages.map((page) => (
                            <tr key={`${page.name}-${page.url || "none"}`}>
                              <td>
                                <strong>{page.name}</strong>
                                <small>{page.url || "-"}</small>
                              </td>
                              <td>{formatNumber(page.views)}</td>
                              <td>{formatNumber(page.users)}</td>
                              <td>{formatNumber(page.sessions)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              </div>
            </>
          ) : (
            <StateBlock type="empty" title="No usage analytics" message="Usage analytics will appear after the first query completes." />
          )}
        </section>
      ) : activeTab === "roles" ? (
        <section className="panel role-admin-panel">
          <div className="panel-heading">
            <div>
              <p>Directory authorization</p>
              <h3>App Role Control Panel</h3>
            </div>
            <button className="ghost-button" disabled={Boolean(roleBusyAction)} onClick={refreshRoles} type="button">
              <RefreshCw size={16} aria-hidden="true" />
              Refresh roles
            </button>
          </div>

          {roleError && (
            <div className="api-banner access-admin-feedback" role="status">
              <span>{roleError}</span>
            </div>
          )}

          <div className="role-admin-grid">
            <div className="role-grant-card">
              <div className="station-picker-heading">
                <span>Grant role</span>
                <strong>{selectedPrincipal ? selectedPrincipal.principalType : "User or group"}</strong>
              </div>
              <div className="search-field">
                <Search size={16} aria-hidden="true" />
                <input
                  aria-label="Search users or groups"
                  onChange={(event) => setPrincipalSearchTerm(event.target.value)}
                  placeholder="Search user or group"
                  type="search"
                  value={principalSearchTerm}
                />
              </div>
              <div className="principal-result-list">
                {principalResults.map((principal) => (
                  <button
                    className={selectedPrincipalId === principal.id ? "principal-result active" : "principal-result"}
                    key={principal.id}
                    onClick={() => setSelectedPrincipalId(principal.id)}
                    type="button"
                  >
                    <span>
                      <strong>{principal.displayName}</strong>
                      <small>{principalIdentity(principal)}</small>
                    </span>
                    <em>{principal.principalType}</em>
                  </button>
                ))}
                {principalSearchTerm.trim().length >= 2 && principalResults.length === 0 && (
                  <div className="empty-inline">No users or groups found.</div>
                )}
              </div>
              <label className="role-select-field">
                <span>App Role</span>
                <select value={selectedRoleId || ""} onChange={(event) => setSelectedRoleId(event.target.value)}>
                  {roles.map((role) => <option key={role.id} value={role.id}>{role.displayName} ({role.value})</option>)}
                </select>
              </label>
              <button className="primary-button" disabled={Boolean(roleBusyAction) || !selectedPrincipalId || !selectedRoleId} onClick={grantRole} type="button">
                <ShieldCheck size={16} aria-hidden="true" />
                Grant App Role
              </button>
            </div>

            <div className="role-assignment-card">
              <div className="station-picker-heading">
                <span>Current assignments</span>
                <strong>{assignments.length} active</strong>
              </div>
              <div className="role-count-grid">
                {roleCounts.map(({ role, count }) => (
                  <span className="role-count-pill" key={role.id}>
                    <strong>{count}</strong>
                    {role.value}
                  </span>
                ))}
              </div>
              <div className="role-assignment-list">
                {assignments.length === 0 ? (
                  <div className="empty-inline">No managed App Role assignments.</div>
                ) : assignments.map((assignment) => (
                  <div className="role-assignment-row" key={assignment.id || assignmentIdentity(assignment)}>
                    <span>
                      <strong>{assignment.principalDisplayName}</strong>
                      <small>{assignment.roleValue || assignment.appRoleId} - {assignment.principalType}</small>
                    </span>
                    <button
                      className="ghost-button danger-button"
                      disabled={Boolean(roleBusyAction)}
                      onClick={() => revokeRole(assignment.id)}
                      type="button"
                    >
                      Revoke
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      ) : (
        <div className="access-admin-grid">
          <section className="panel access-user-panel">
            <div className="access-admin-toolbar access-admin-toolbar--stacked">
              <div className="search-field">
                <Search size={16} aria-hidden="true" />
                <input
                  aria-label="Search users"
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search users, email, Station_SK"
                  type="search"
                  value={searchTerm}
                />
              </div>
              <div className="access-filter-chips" aria-label="User status filters">
                {filterOptions.map((option) => (
                  <button
                    className={userFilter === option.id ? "access-filter-chip active" : "access-filter-chip"}
                    key={option.id}
                    onClick={() => setUserFilter(option.id)}
                    type="button"
                  >
                    <span>{filterLabels[option.id]}</span>
                    <strong>{option.count}</strong>
                  </button>
                ))}
              </div>
            </div>

            <div className="access-list-heading">
              <span>{filteredUsers.length} of {counts.total} users</span>
              <strong>{filterLabels[userFilter]}</strong>
            </div>

            <div className="access-user-list">
              {filteredUsers.map((user) => {
                const stationCount = activeStationCount(user);
                return (
                  <button
                    className={selectedUser?.entraObjectId === user.entraObjectId ? "access-user-card active" : "access-user-card"}
                    key={user.entraObjectId}
                    onClick={() => setSelectedUserId(user.entraObjectId)}
                    type="button"
                  >
                    <span className="access-user-card__main">
                      <strong>{user.displayName}</strong>
                      <small>{userIdentity(user)}</small>
                    </span>
                    <AccessStatusChip status={user.accessStatus} />
                    <span className={stationCount === 0 ? "access-user-card__stations empty" : "access-user-card__stations"}>
                      <Store size={14} aria-hidden="true" />
                      {stationCount}
                    </span>
                    <small className="access-user-card__date">Requested {formatDate(user.requestedAt)}</small>
                  </button>
                );
              })}
              {filteredUsers.length === 0 && (
                <div className="empty-inline">No portal users match the current filter.</div>
              )}
            </div>
          </section>

          <section className="panel access-detail-panel">
            {selectedUser ? (
              <>
                <div className="panel-heading access-detail-heading">
                  <div>
                    <p>Selected user</p>
                    <h3>{selectedUser.displayName}</h3>
                  </div>
                  <div className="access-detail-heading__actions">
                    <AccessStatusChip status={selectedUser.accessStatus} />
                    <UserCog size={18} aria-hidden="true" />
                  </div>
                </div>

                {activeStationCount(selectedUser) === 0 && (
                  <div className="access-warning" role="status">
                    <AlertTriangle size={16} aria-hidden="true" />
                    <span>Station access is empty. Assign at least one Station_SK before activating this user.</span>
                  </div>
                )}

                <div className="access-profile-grid">
                  <span>Email</span>
                  <strong>{userIdentity(selectedUser)}</strong>
                  <span>User type</span>
                  <strong>{selectedUser.userType || "-"}</strong>
                  <span>Requested</span>
                  <strong>{formatDate(selectedUser.requestedAt)}</strong>
                  <span>Last sign-in</span>
                  <strong>{formatDate(selectedUser.lastSignedInAt)}</strong>
                  <span>Object ID</span>
                  <strong>{selectedUser.entraObjectId}</strong>
                </div>

                <div className="access-status-actions">
                  {accessStatuses.map((status) => (
                    <button
                      className={status === "ACTIVE" ? "primary-button" : status === "DENIED" || status === "SUSPENDED" ? "ghost-button danger-button" : "ghost-button"}
                      disabled={Boolean(busyAction) || selectedUser.accessStatus === status}
                      key={status}
                      onClick={() => setStatus(status)}
                      type="button"
                    >
                      {status === "ACTIVE" && <CheckCircle2 size={16} aria-hidden="true" />}
                      {status === "PENDING" && <Clock3 size={16} aria-hidden="true" />}
                      {status === "SUSPENDED" && <Ban size={16} aria-hidden="true" />}
                      {status === "DENIED" && <XCircle size={16} aria-hidden="true" />}
                      {status}
                    </button>
                  ))}
                </div>

                <div className="station-grant-form station-grant-form--multi">
                  <div className="station-picker-heading">
                    <span>Station_SK</span>
                    <strong>{selectedStationSks.length} selected - {filteredStations.length} visible</strong>
                  </div>

                  <div className="station-picker-tools">
                    <div className="search-field">
                      <Search size={16} aria-hidden="true" />
                      <input
                        aria-label="Search stations"
                        onChange={(event) => setStationSearchTerm(event.target.value)}
                        placeholder="Search Station_SK, name, province"
                        type="search"
                        value={stationSearchTerm}
                      />
                    </div>
                    <label className="access-toggle">
                      <input
                        checked={hideAssignedStations}
                        onChange={(event) => setHideAssignedStations(event.target.checked)}
                        type="checkbox"
                      />
                      <span>Hide assigned</span>
                    </label>
                  </div>

                  <div className="station-picker-actions">
                    <button
                      className="ghost-button"
                      disabled={Boolean(busyAction) || selectableFilteredStationSks.length === 0}
                      onClick={selectFilteredStations}
                      type="button"
                    >
                      <CheckSquare size={16} aria-hidden="true" />
                      Select visible
                    </button>
                    <button
                      className="ghost-button"
                      disabled={Boolean(busyAction) || selectedStationSks.length === 0}
                      onClick={() => setSelectedStationSks([])}
                      type="button"
                    >
                      <X size={16} aria-hidden="true" />
                      Clear
                    </button>
                  </div>

                  <div className="station-picker-list" role="group" aria-label="Station_SK assignments">
                    {filteredStations.map((station) => {
                      const stationSkValue = String(station.stationSk);
                      const isAssigned = activeStationSks.has(stationSkValue);
                      return (
                        <label className={isAssigned ? "station-picker-option assigned" : "station-picker-option"} key={station.stationSk}>
                          <input
                            checked={selectedStationSks.includes(stationSkValue)}
                            disabled={Boolean(busyAction)}
                            onChange={() => toggleStationSk(station.stationSk)}
                            type="checkbox"
                          />
                          <span>
                            <strong>{station.stationSk} - {stationName(station)}</strong>
                            <small>{stationMeta(station)}</small>
                          </span>
                          {isAssigned && <em>Assigned</em>}
                        </label>
                      );
                    })}
                    {filteredStations.length === 0 && (
                      <div className="empty-inline">No stations match the current search.</div>
                    )}
                  </div>
                  <label>
                    <span>Access</span>
                    <select value={accessRole} onChange={(event) => setAccessRole(event.target.value as PortalStationAccessRole)}>
                      {accessRoles.map((role) => <option key={role} value={role}>{role}</option>)}
                    </select>
                  </label>
                  <button className="primary-button" disabled={Boolean(busyAction) || selectedStationSks.length === 0} onClick={grantStation} type="button">
                    <Store size={16} aria-hidden="true" />
                    Grant stations
                  </button>
                </div>

                <div className="station-assignment-list">
                  <div className="event-history-heading">
                    <span>Station assignments</span>
                    <strong>{activeStationCount(selectedUser)} active</strong>
                  </div>
                  {selectedUser.stations.length === 0 ? (
                    <div className="empty-inline">No Station_SK assignments.</div>
                  ) : (
                    selectedUser.stations.map((station) => (
                      <div className={station.isActive ? "station-assignment-row" : "station-assignment-row inactive"} key={station.accessId}>
                        <span>
                          <strong>{station.stationSk} - {stationName(station)}</strong>
                          <small>{stationMeta(station)}</small>
                        </span>
                        <span>{station.accessRole}</span>
                        <button
                          className="ghost-button danger-button"
                          disabled={Boolean(busyAction) || !station.isActive}
                          onClick={() => revokeStation(station.accessId)}
                          type="button"
                        >
                          Revoke
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : (
              <StateBlock type="empty" title="No portal users" message="External sign-up records will appear here after first login." />
            )}
          </section>
        </div>
      )}
    </section>
  );
}
